import { Suspense, lazy, useMemo } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
import AdminGuestGuard from '../guards/AdminGuestGuard';
import AuthGuard from '../guards/AuthGuard';
// import RoleBasedGuard from '../guards/RoleBasedGuard';
// components
import LoadingScreen from '../components/LoadingScreen';
import useAuth from '../hooks/useAuth';
import { filterNavs } from '../utils/formatObject';

// ----------------------------------------------------------------------

const Loadable = (Component) => (props) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  return (
    <Suspense fallback={<LoadingScreen isDashboard={pathname.includes('/dashboard')} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  const { user } = useAuth();

  const filteredRoutes = useMemo(() => {
    const initialRoutes = [
      // ADMIN LOGIN
      {
        path: 'admin',
        children: [
          {
            path: 'auth/login',
            element: (
              <AdminGuestGuard>
                <AdminLogin />
              </AdminGuestGuard>
            ),
          },
          // CAMPAIGN
          {
            path: 'campaign',
            element: (
              <AuthGuard>
                <DashboardLayout />
              </AuthGuard>
            ),
            children: [
              { element: <CampaignList />, index: true },
              { path: ':campaignId', element: <Campaign /> },
            ],
          },
          // USER
          {
            path: 'user',
            element: (
              <AuthGuard>
                <DashboardLayout />
              </AuthGuard>
            ),
            children: [
              { element: <UserList />, index: true },
              // { path: ':campaignId', element: <Campaign /> },
            ],
          },
        ],
      },
      //
      {
        path: 'spin',
        children: [
          {
            path: ':campaignId/:username/:password',
            element: <SpinPage />,
          },
        ],
      },

      // Main Routes
      {
        path: '*',
        element: <LogoOnlyLayout />,
        children: [
          { path: 'coming-soon', element: <ComingSoon /> },
          { path: 'maintenance', element: <Maintenance /> },
          { path: '500', element: <Page500 /> },
          { path: '404', element: <Page404 /> },
          { path: '403', element: <Page403 /> },
          { path: '402', element: <Page402 /> },
          { path: '*', element: <Navigate to="/404" replace /> },
        ],
      },
      { path: '*', element: <Navigate to="/404" replace /> },
    ];

    const _filteredRoutes = user?.role ? filterNavs(initialRoutes, user?.role) : initialRoutes;

    return _filteredRoutes;
  }, [user]);

  return useRoutes(filteredRoutes);
}

// SPIN
const SpinPage = Loadable(lazy(() => import('../pages/spin/SpinPage')));

// ADMIN AUTHENTICATION
const AdminLogin = Loadable(lazy(() => import('../pages/adminAuth/AdminLogin')));

// CAMPAIGN
const CampaignList = Loadable(lazy(() => import('../pages/campaign/CampaignList')));
const Campaign = Loadable(lazy(() => import('../pages/campaign/Campaign')));

// USERS
const UserList = Loadable(lazy(() => import('../pages/user/UserList')));

// TEST RENDER PAGE BY ROLE
const PermissionDenied = Loadable(lazy(() => import('../pages/dashboard/PermissionDenied')));

// MAIN
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page402 = Loadable(lazy(() => import('../pages/Page402')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));
